import type {PdfFile} from './proposal-pdf';

/**
 * ONE PDF REQUEST PER RELEASED VERSION, and it always lets go of "preparing".
 *
 * Jacky, 18 Oct 2026 (Rosie Prom): *"CRM couldn't build the proposal PDF, so it endlessly cannot send, I had to
 * untick"*. The Proposal was re-released three times in eight seconds (v37 → v39) while its PDF took ~18 s to draw.
 * The Send screen's effect dropped the in-flight request when the version changed but never cleared `preparing`,
 * and it refused to start a new request while `preparing` was set — so the attachment waited forever and Send with
 * it. The server drew v39 fine every time; the trap was entirely the screen's state.
 *
 * A newer version cancels the older request and starts its own; a late answer for an older version is ignored; the
 * request that is current always clears `preparing` when it ends, however it ends.
 */
export type LoadedPdf = {name: string; size: number; content_base64: string; version: number};
export function createPdfAttachmentLoader(
  fetchPdf: (version: number, signal: AbortSignal) => Promise<PdfFile>,
  on: {preparing: (busy: boolean) => void; file: (file: LoadedPdf) => void; error: (failure: {version: number; reason: string} | null) => void},
) {
  let current: {version: number; ctrl: AbortController} | null = null;
  return {
    /** Draw this version's PDF unless it is already being drawn. */
    request(version: number) {
      if (current?.version === version) return;
      current?.ctrl.abort();
      const ctrl = new AbortController();
      current = {version, ctrl};
      on.error(null);
      on.preparing(true);
      fetchPdf(version, ctrl.signal)
        .then((f) => { if (current?.ctrl !== ctrl) return; if (f.ok) on.file({name: f.name, size: f.size, content_base64: f.content_base64, version}); else on.error({version, reason: f.reason}); })
        .catch((e: unknown) => { if (current?.ctrl === ctrl) on.error({version, reason: e instanceof Error ? e.message : String(e)}); })
        .finally(() => { if (current?.ctrl === ctrl) { current = null; on.preparing(false); } });
    },
    /** Nothing wanted any more (unticked, channel changed, screen closed). */
    cancel() {
      if (!current) return;
      current.ctrl.abort();
      current = null;
      on.preparing(false);
    },
  };
}
